import { useEffect, useState, useRef, useMemo } from "react";
import { Truck, Save, X, Building2, User, Phone, Mail, IdCard } from "lucide-react";
import Swal from "sweetalert2";
import api from "../services/api";

export default function SupplierForm({ onSaved, editingSupplier, onCancel }) {
  const nombreRef = useRef(null);

  const [form, setForm] = useState({
    nombre: "",
    nombre_comercial: "",
    contacto: "",
    telefono: "",
    email: "",
    direccion: "",
    departamento: "",
    municipio: "",
    distrito: "",
    nit: "",
    nrc: "",
    giro: "",
    tipo_documento: "36",
    numero_documento: "",
  });

  const [loading, setLoading] = useState(false);

  // ✅ CAT-012 Departamentos
  const departamentos = [
    { codigo: "00", nombre: "Otro (Para extranjeros)" },
    { codigo: "01", nombre: "AHUACHAPAN" },
    { codigo: "02", nombre: "SANTA ANA" },
    { codigo: "03", nombre: "SONSONATE" },
    { codigo: "04", nombre: "CHALATENANGO" },
    { codigo: "05", nombre: "LA LIBERTAD" },
    { codigo: "06", nombre: "SAN SALVADOR" },
    { codigo: "07", nombre: "CUSCATLAN" },
    { codigo: "08", nombre: "LA PAZ" },
    { codigo: "09", nombre: "CABAÑAS" },
    { codigo: "10", nombre: "SAN VICENTE" },
    { codigo: "11", nombre: "USULUTAN" },
    { codigo: "12", nombre: "SAN MIGUEL" },
    { codigo: "13", nombre: "MORAZAN" },
    { codigo: "14", nombre: "LA UNION" },
  ];

  // ✅ CAT-013 Municipio — códigos OFICIALES tomados del Excel de Hacienda
  // (Catálogos - Facturación Electrónica v1.1). Verificado línea por línea.
  const todosMunicipios = [
    { departamentoCodigo: "00", codigo: "00", nombre: "Otro (Para extranjeros)" },

    { departamentoCodigo: "01", codigo: "13", nombre: "AHUACHAPAN NORTE" },
    { departamentoCodigo: "01", codigo: "14", nombre: "AHUACHAPAN CENTRO" },
    { departamentoCodigo: "01", codigo: "15", nombre: "AHUACHAPAN SUR" },

    { departamentoCodigo: "02", codigo: "14", nombre: "SANTA ANA NORTE" },
    { departamentoCodigo: "02", codigo: "15", nombre: "SANTA ANA CENTRO" },
    { departamentoCodigo: "02", codigo: "16", nombre: "SANTA ANA ESTE" },
    { departamentoCodigo: "02", codigo: "17", nombre: "SANTA ANA OESTE" },

    { departamentoCodigo: "03", codigo: "17", nombre: "SONSONATE NORTE" },
    { departamentoCodigo: "03", codigo: "18", nombre: "SONSONATE CENTRO" },
    { departamentoCodigo: "03", codigo: "19", nombre: "SONSONATE ESTE" },
    { departamentoCodigo: "03", codigo: "20", nombre: "SONSONATE OESTE" },

    { departamentoCodigo: "04", codigo: "34", nombre: "CHALATENANGO NORTE" },
    { departamentoCodigo: "04", codigo: "35", nombre: "CHALATENANGO CENTRO" },
    { departamentoCodigo: "04", codigo: "36", nombre: "CHALATENANGO SUR" },

    { departamentoCodigo: "05", codigo: "23", nombre: "LA LIBERTAD NORTE" },
    { departamentoCodigo: "05", codigo: "24", nombre: "LA LIBERTAD CENTRO" },
    { departamentoCodigo: "05", codigo: "25", nombre: "LA LIBERTAD OESTE" },
    { departamentoCodigo: "05", codigo: "26", nombre: "LA LIBERTAD ESTE" },
    { departamentoCodigo: "05", codigo: "27", nombre: "LA LIBERTAD COSTA" },
    { departamentoCodigo: "05", codigo: "28", nombre: "LA LIBERTAD SUR" },

    { departamentoCodigo: "06", codigo: "20", nombre: "SAN SALVADOR NORTE" },
    { departamentoCodigo: "06", codigo: "21", nombre: "SAN SALVADOR OESTE" },
    { departamentoCodigo: "06", codigo: "22", nombre: "SAN SALVADOR ESTE" },
    { departamentoCodigo: "06", codigo: "23", nombre: "SAN SALVADOR CENTRO" },
    { departamentoCodigo: "06", codigo: "24", nombre: "SAN SALVADOR SUR" },

    { departamentoCodigo: "07", codigo: "17", nombre: "CUSCATLAN NORTE" },
    { departamentoCodigo: "07", codigo: "18", nombre: "CUSCATLAN SUR" },

    { departamentoCodigo: "08", codigo: "23", nombre: "LA PAZ OESTE" },
    { departamentoCodigo: "08", codigo: "24", nombre: "LA PAZ CENTRO" },
    { departamentoCodigo: "08", codigo: "25", nombre: "LA PAZ ESTE" },

    { departamentoCodigo: "09", codigo: "10", nombre: "CABAÑAS ESTE" },
    { departamentoCodigo: "09", codigo: "11", nombre: "CABAÑAS OESTE" },

    { departamentoCodigo: "10", codigo: "14", nombre: "SAN VICENTE NORTE" },
    { departamentoCodigo: "10", codigo: "15", nombre: "SAN VICENTE SUR" },

    { departamentoCodigo: "11", codigo: "24", nombre: "USULUTAN NORTE" },
    { departamentoCodigo: "11", codigo: "25", nombre: "USULUTAN ESTE" },
    { departamentoCodigo: "11", codigo: "26", nombre: "USULUTAN OESTE" },

    { departamentoCodigo: "12", codigo: "21", nombre: "SAN MIGUEL NORTE" },
    { departamentoCodigo: "12", codigo: "22", nombre: "SAN MIGUEL CENTRO" },
    { departamentoCodigo: "12", codigo: "23", nombre: "SAN MIGUEL OESTE" },

    { departamentoCodigo: "13", codigo: "27", nombre: "MORAZAN NORTE" },
    { departamentoCodigo: "13", codigo: "28", nombre: "MORAZAN SUR" },

    { departamentoCodigo: "14", codigo: "19", nombre: "LA UNION NORTE" },
    { departamentoCodigo: "14", codigo: "20", nombre: "LA UNION SUR" },
  ];

  // ✅ Distritos (CAT-008, pueblo específico) — con su municipioCodigo (zona, CAT-013)
  // Mapeo oficial según Decreto Legislativo 762 (Ley Especial de Reestructuración Municipal)
  const todosDistritos = [
    { departamentoCodigo: "00", municipioCodigo: "00", codigo: "00", nombre: "Otro (Para extranjeros)" },
    { departamentoCodigo: "01", municipioCodigo: "14", codigo: "01", nombre: "AHUACHAPÁN" },
    { departamentoCodigo: "01", municipioCodigo: "14", codigo: "02", nombre: "APANECA" },
    { departamentoCodigo: "01", municipioCodigo: "13", codigo: "03", nombre: "ATIQUIZAYA" },
    { departamentoCodigo: "01", municipioCodigo: "14", codigo: "04", nombre: "CONCEPCIÓN DE ATACO" },
    { departamentoCodigo: "01", municipioCodigo: "13", codigo: "05", nombre: "EL REFUGIO" },
    { departamentoCodigo: "01", municipioCodigo: "15", codigo: "06", nombre: "GUAYMANGO" },
    { departamentoCodigo: "01", municipioCodigo: "15", codigo: "07", nombre: "JUJUTLA" },
    { departamentoCodigo: "01", municipioCodigo: "15", codigo: "08", nombre: "SAN FRANCISCO MENÉNDEZ" },
    { departamentoCodigo: "01", municipioCodigo: "13", codigo: "09", nombre: "SAN LORENZO" },
    { departamentoCodigo: "01", municipioCodigo: "15", codigo: "10", nombre: "SAN PEDRO PUXTLA" },
    { departamentoCodigo: "01", municipioCodigo: "14", codigo: "11", nombre: "TACUBA" },
    { departamentoCodigo: "01", municipioCodigo: "13", codigo: "12", nombre: "TURÍN" },
    { departamentoCodigo: "02", municipioCodigo: "17", codigo: "01", nombre: "CANDELARIA DE LA FRONTERA" },
    { departamentoCodigo: "02", municipioCodigo: "16", codigo: "02", nombre: "COATEPEQUE" },
    { departamentoCodigo: "02", municipioCodigo: "17", codigo: "03", nombre: "CHALCHUAPA" },
    { departamentoCodigo: "02", municipioCodigo: "16", codigo: "04", nombre: "EL CONGO" },
    { departamentoCodigo: "02", municipioCodigo: "17", codigo: "05", nombre: "EL PORVENIR" },
    { departamentoCodigo: "02", municipioCodigo: "14", codigo: "06", nombre: "MASAHUAT" },
    { departamentoCodigo: "02", municipioCodigo: "14", codigo: "07", nombre: "METAPÁN" },
    { departamentoCodigo: "02", municipioCodigo: "17", codigo: "08", nombre: "SAN ANTONIO PAJONAL" },
    { departamentoCodigo: "02", municipioCodigo: "17", codigo: "09", nombre: "SAN SEBASTIÁN SALITRILLO" },
    { departamentoCodigo: "02", municipioCodigo: "15", codigo: "10", nombre: "SANTA ANA" },
    { departamentoCodigo: "02", municipioCodigo: "14", codigo: "11", nombre: "STA ROSA GUACHI" },
    { departamentoCodigo: "02", municipioCodigo: "17", codigo: "12", nombre: "STGO D LA FRONT" },
    { departamentoCodigo: "02", municipioCodigo: "14", codigo: "13", nombre: "TEXISTEPEQUE" },
    { departamentoCodigo: "03", municipioCodigo: "20", codigo: "01", nombre: "ACAJUTLA" },
    { departamentoCodigo: "03", municipioCodigo: "19", codigo: "02", nombre: "ARMENIA" },
    { departamentoCodigo: "03", municipioCodigo: "19", codigo: "03", nombre: "CALUCO" },
    { departamentoCodigo: "03", municipioCodigo: "19", codigo: "04", nombre: "CUISNAHUAT" },
    { departamentoCodigo: "03", municipioCodigo: "19", codigo: "05", nombre: "STA I ISHUATAN" },
    { departamentoCodigo: "03", municipioCodigo: "19", codigo: "06", nombre: "IZALCO" },
    { departamentoCodigo: "03", municipioCodigo: "17", codigo: "07", nombre: "JUAYÚA" },
    { departamentoCodigo: "03", municipioCodigo: "17", codigo: "08", nombre: "NAHUIZALCO" },
    { departamentoCodigo: "03", municipioCodigo: "18", codigo: "09", nombre: "NAHULINGO" },
    { departamentoCodigo: "03", municipioCodigo: "17", codigo: "10", nombre: "SALCOATITÁN" },
    { departamentoCodigo: "03", municipioCodigo: "18", codigo: "11", nombre: "SAN ANTONIO DEL MONTE" },
    { departamentoCodigo: "03", municipioCodigo: "19", codigo: "12", nombre: "SAN JULIÁN" },
    { departamentoCodigo: "03", municipioCodigo: "17", codigo: "13", nombre: "STA C MASAHUAT" },
    { departamentoCodigo: "03", municipioCodigo: "18", codigo: "14", nombre: "SANTO DOMINGO GUZMÁN" },
    { departamentoCodigo: "03", municipioCodigo: "18", codigo: "15", nombre: "SONSONATE" },
    { departamentoCodigo: "03", municipioCodigo: "18", codigo: "16", nombre: "SONZACATE" },
    { departamentoCodigo: "04", municipioCodigo: "35", codigo: "01", nombre: "AGUA CALIENTE" },
    { departamentoCodigo: "04", municipioCodigo: "36", codigo: "02", nombre: "ARCATAO" },
    { departamentoCodigo: "04", municipioCodigo: "36", codigo: "03", nombre: "AZACUALPA" },
    { departamentoCodigo: "04", municipioCodigo: "34", codigo: "04", nombre: "CITALÁ" },
    { departamentoCodigo: "04", municipioCodigo: "36", codigo: "05", nombre: "COMALAPA" },
    { departamentoCodigo: "04", municipioCodigo: "36", codigo: "06", nombre: "CONCEPCIÓN QUEZALTEPEQUE" },
    { departamentoCodigo: "04", municipioCodigo: "36", codigo: "07", nombre: "CHALATENANGO" },
    { departamentoCodigo: "04", municipioCodigo: "35", codigo: "08", nombre: "DULCE NOM MARÍA" },
    { departamentoCodigo: "04", municipioCodigo: "36", codigo: "09", nombre: "EL CARRIZAL" },
    { departamentoCodigo: "04", municipioCodigo: "35", codigo: "10", nombre: "EL PARAÍSO" },
    { departamentoCodigo: "04", municipioCodigo: "36", codigo: "11", nombre: "LA LAGUNA" },
    { departamentoCodigo: "04", municipioCodigo: "34", codigo: "12", nombre: "LA PALMA" },
    { departamentoCodigo: "04", municipioCodigo: "35", codigo: "13", nombre: "LA REINA" },
    { departamentoCodigo: "04", municipioCodigo: "36", codigo: "14", nombre: "LAS VUELTAS" },
    { departamentoCodigo: "04", municipioCodigo: "36", codigo: "15", nombre: "NOMBRE DE JESUS" },
    { departamentoCodigo: "04", municipioCodigo: "35", codigo: "16", nombre: "NVA CONCEPCIÓN" },
    { departamentoCodigo: "04", municipioCodigo: "36", codigo: "17", nombre: "NUEVA TRINIDAD" },
    { departamentoCodigo: "04", municipioCodigo: "36", codigo: "18", nombre: "OJOS DE AGUA" },
    { departamentoCodigo: "04", municipioCodigo: "36", codigo: "19", nombre: "POTONICO" },
    { departamentoCodigo: "04", municipioCodigo: "36", codigo: "20", nombre: "SAN ANT LA CRUZ" },
    { departamentoCodigo: "04", municipioCodigo: "36", codigo: "21", nombre: "SAN ANT RANCHOS" },
    { departamentoCodigo: "04", municipioCodigo: "34", codigo: "22", nombre: "SAN FERNANDO" },
    { departamentoCodigo: "04", municipioCodigo: "36", codigo: "23", nombre: "SAN FRANCISCO LEMPA" },
    { departamentoCodigo: "04", municipioCodigo: "35", codigo: "24", nombre: "SAN FRANCISCO MORAZÁN" },
    { departamentoCodigo: "04", municipioCodigo: "34", codigo: "25", nombre: "SAN IGNACIO" },
    { departamentoCodigo: "04", municipioCodigo: "36", codigo: "26", nombre: "SAN I LABRADOR" },
    { departamentoCodigo: "04", municipioCodigo: "36", codigo: "27", nombre: "SAN J CANCASQUE" },
    { departamentoCodigo: "04", municipioCodigo: "36", codigo: "28", nombre: "SAN JOSE FLORES" },
    { departamentoCodigo: "04", municipioCodigo: "36", codigo: "29", nombre: "SAN LUIS CARMEN" },
    { departamentoCodigo: "04", municipioCodigo: "36", codigo: "30", nombre: "SN MIG MERCEDES" },
    { departamentoCodigo: "04", municipioCodigo: "35", codigo: "31", nombre: "SAN RAFAEL" },
    { departamentoCodigo: "04", municipioCodigo: "35", codigo: "32", nombre: "SANTA RITA" },
    { departamentoCodigo: "04", municipioCodigo: "35", codigo: "33", nombre: "TEJUTLA" },
    { departamentoCodigo: "05", municipioCodigo: "26", codigo: "01", nombre: "ANTGO CUSCATLÁN" },
    { departamentoCodigo: "05", municipioCodigo: "24", codigo: "02", nombre: "CIUDAD ARCE" },
    { departamentoCodigo: "05", municipioCodigo: "25", codigo: "03", nombre: "COLON" },
    { departamentoCodigo: "05", municipioCodigo: "28", codigo: "04", nombre: "COMASAGUA" },
    { departamentoCodigo: "05", municipioCodigo: "27", codigo: "05", nombre: "CHILTIUPAN" },
    { departamentoCodigo: "05", municipioCodigo: "26", codigo: "06", nombre: "HUIZÚCAR" },
    { departamentoCodigo: "05", municipioCodigo: "25", codigo: "07", nombre: "JAYAQUE" },
    { departamentoCodigo: "05", municipioCodigo: "27", codigo: "08", nombre: "JICALAPA" },
    { departamentoCodigo: "05", municipioCodigo: "27", codigo: "09", nombre: "LA LIBERTAD" },
    { departamentoCodigo: "05", municipioCodigo: "26", codigo: "10", nombre: "NUEVO CUSCATLÁN" },
    { departamentoCodigo: "05", municipioCodigo: "28", codigo: "11", nombre: "SANTA TECLA" },
    { departamentoCodigo: "05", municipioCodigo: "23", codigo: "12", nombre: "QUEZALTEPEQUE" },
    { departamentoCodigo: "05", municipioCodigo: "25", codigo: "13", nombre: "SACACOYO" },
    { departamentoCodigo: "05", municipioCodigo: "26", codigo: "14", nombre: "SN J VILLANUEVA" },
    { departamentoCodigo: "05", municipioCodigo: "24", codigo: "15", nombre: "SAN JUAN OPICO" },
    { departamentoCodigo: "05", municipioCodigo: "23", codigo: "16", nombre: "SAN MATÍAS" },
    { departamentoCodigo: "05", municipioCodigo: "23", codigo: "17", nombre: "SAN P TACACHICO" },
    { departamentoCodigo: "05", municipioCodigo: "27", codigo: "18", nombre: "TAMANIQUE" },
    { departamentoCodigo: "05", municipioCodigo: "25", codigo: "19", nombre: "TALNIQUE" },
    { departamentoCodigo: "05", municipioCodigo: "27", codigo: "20", nombre: "TEOTEPEQUE" },
    { departamentoCodigo: "05", municipioCodigo: "25", codigo: "21", nombre: "TEPECOYO" },
    { departamentoCodigo: "05", municipioCodigo: "26", codigo: "22", nombre: "ZARAGOZA" },
    { departamentoCodigo: "06", municipioCodigo: "20", codigo: "01", nombre: "AGUILARES" },
    { departamentoCodigo: "06", municipioCodigo: "21", codigo: "02", nombre: "APOPA" },
    { departamentoCodigo: "06", municipioCodigo: "23", codigo: "03", nombre: "AYUTUXTEPEQUE" },
    { departamentoCodigo: "06", municipioCodigo: "23", codigo: "04", nombre: "CUSCATANCINGO" },
    { departamentoCodigo: "06", municipioCodigo: "20", codigo: "05", nombre: "EL PAISNAL" },
    { departamentoCodigo: "06", municipioCodigo: "20", codigo: "06", nombre: "GUAZAPA" },
    { departamentoCodigo: "06", municipioCodigo: "22", codigo: "07", nombre: "ILOPANGO" },
    { departamentoCodigo: "06", municipioCodigo: "23", codigo: "08", nombre: "MEJICANOS" },
    { departamentoCodigo: "06", municipioCodigo: "21", codigo: "09", nombre: "NEJAPA" },
    { departamentoCodigo: "06", municipioCodigo: "24", codigo: "10", nombre: "PANCHIMALCO" },
    { departamentoCodigo: "06", municipioCodigo: "24", codigo: "11", nombre: "ROSARIO DE MORA" },
    { departamentoCodigo: "06", municipioCodigo: "24", codigo: "12", nombre: "SAN MARCOS" },
    { departamentoCodigo: "06", municipioCodigo: "22", codigo: "13", nombre: "SAN MARTIN" },
    { departamentoCodigo: "06", municipioCodigo: "23", codigo: "14", nombre: "SAN SALVADOR" },
    { departamentoCodigo: "06", municipioCodigo: "24", codigo: "15", nombre: "STG TEXACUANGOS" },
    { departamentoCodigo: "06", municipioCodigo: "24", codigo: "16", nombre: "SANTO TOMAS" },
    { departamentoCodigo: "06", municipioCodigo: "22", codigo: "17", nombre: "SOYAPANGO" },
    { departamentoCodigo: "06", municipioCodigo: "22", codigo: "18", nombre: "TONACATEPEQUE" },
    { departamentoCodigo: "06", municipioCodigo: "23", codigo: "19", nombre: "CIUDAD DELGADO" },
    { departamentoCodigo: "07", municipioCodigo: "18", codigo: "01", nombre: "CANDELARIA" },
    { departamentoCodigo: "07", municipioCodigo: "18", codigo: "02", nombre: "COJUTEPEQUE" },
    { departamentoCodigo: "07", municipioCodigo: "18", codigo: "03", nombre: "EL CARMEN" },
    { departamentoCodigo: "07", municipioCodigo: "18", codigo: "04", nombre: "EL ROSARIO" },
    { departamentoCodigo: "07", municipioCodigo: "18", codigo: "05", nombre: "MONTE SAN JUAN" },
    { departamentoCodigo: "07", municipioCodigo: "17", codigo: "06", nombre: "ORAT CONCEPCIÓN" },
    { departamentoCodigo: "07", municipioCodigo: "17", codigo: "07", nombre: "SAN B PERULAPIA" },
    { departamentoCodigo: "07", municipioCodigo: "18", codigo: "08", nombre: "SAN CRISTÓBAL" },
    { departamentoCodigo: "07", municipioCodigo: "17", codigo: "09", nombre: "SAN J GUAYABAL" },
    { departamentoCodigo: "07", municipioCodigo: "17", codigo: "10", nombre: "SAN P PERULAPÁN" },
    { departamentoCodigo: "07", municipioCodigo: "18", codigo: "11", nombre: "SAN RAF CEDROS" },
    { departamentoCodigo: "07", municipioCodigo: "18", codigo: "12", nombre: "SAN RAMON" },
    { departamentoCodigo: "07", municipioCodigo: "18", codigo: "13", nombre: "STA C ANALQUITO" },
    { departamentoCodigo: "07", municipioCodigo: "18", codigo: "14", nombre: "STA C MICHAPA" },
    { departamentoCodigo: "07", municipioCodigo: "17", codigo: "15", nombre: "SUCHITOTO" },
    { departamentoCodigo: "07", municipioCodigo: "18", codigo: "16", nombre: "TENANCINGO" },
    { departamentoCodigo: "08", municipioCodigo: "23", codigo: "01", nombre: "CUYULTITÁN" },
    { departamentoCodigo: "08", municipioCodigo: "24", codigo: "02", nombre: "EL ROSARIO" },
    { departamentoCodigo: "08", municipioCodigo: "24", codigo: "03", nombre: "JERUSALÉN" },
    { departamentoCodigo: "08", municipioCodigo: "24", codigo: "04", nombre: "MERCED LA CEIBA" },
    { departamentoCodigo: "08", municipioCodigo: "23", codigo: "05", nombre: "OLOCUILTA" },
    { departamentoCodigo: "08", municipioCodigo: "24", codigo: "06", nombre: "PARAÍSO OSORIO" },
    { departamentoCodigo: "08", municipioCodigo: "23", codigo: "07", nombre: "SN ANT MASAHUAT" },
    { departamentoCodigo: "08", municipioCodigo: "24", codigo: "08", nombre: "SAN EMIGDIO" },
    { departamentoCodigo: "08", municipioCodigo: "23", codigo: "09", nombre: "SN FCO CHINAMEC" },
    { departamentoCodigo: "08", municipioCodigo: "25", codigo: "10", nombre: "SAN J NONUALCO" },
    { departamentoCodigo: "08", municipioCodigo: "23", codigo: "11", nombre: "SAN JUAN TALPA" },
    { departamentoCodigo: "08", municipioCodigo: "24", codigo: "12", nombre: "SAN JUAN TEPEZONTES" },
    { departamentoCodigo: "08", municipioCodigo: "23", codigo: "13", nombre: "SAN LUIS TALPA" },
    { departamentoCodigo: "08", municipioCodigo: "24", codigo: "14", nombre: "SAN MIGUEL TEPEZONTES" },
    { departamentoCodigo: "08", municipioCodigo: "24", codigo: "15", nombre: "SAN PEDRO MASAHUAT" },
    { departamentoCodigo: "08", municipioCodigo: "24", codigo: "16", nombre: "SAN PEDRO NONUALCO" },
    { departamentoCodigo: "08", municipioCodigo: "25", codigo: "17", nombre: "SAN R OBRAJUELO" },
    { departamentoCodigo: "08", municipioCodigo: "24", codigo: "18", nombre: "STA MA OSTUMA" },
    { departamentoCodigo: "08", municipioCodigo: "24", codigo: "19", nombre: "STGO NONUALCO" },
    { departamentoCodigo: "08", municipioCodigo: "23", codigo: "20", nombre: "TAPALHUACA" },
    { departamentoCodigo: "08", municipioCodigo: "25", codigo: "21", nombre: "ZACATECOLUCA" },
    { departamentoCodigo: "08", municipioCodigo: "24", codigo: "22", nombre: "SN LUIS LA HERR" },
    { departamentoCodigo: "09", municipioCodigo: "11", codigo: "01", nombre: "CINQUERA" },
    { departamentoCodigo: "09", municipioCodigo: "10", codigo: "02", nombre: "GUACOTECTI" },
    { departamentoCodigo: "09", municipioCodigo: "11", codigo: "03", nombre: "ILOBASCO" },
    { departamentoCodigo: "09", municipioCodigo: "11", codigo: "04", nombre: "JUTIAPA" },
    { departamentoCodigo: "09", municipioCodigo: "10", codigo: "05", nombre: "SAN ISIDRO" },
    { departamentoCodigo: "09", municipioCodigo: "10", codigo: "06", nombre: "SENSUNTEPEQUE" },
    { departamentoCodigo: "09", municipioCodigo: "11", codigo: "07", nombre: "TEJUTEPEQUE" },
    { departamentoCodigo: "09", municipioCodigo: "10", codigo: "08", nombre: "VICTORIA" },
    { departamentoCodigo: "09", municipioCodigo: "10", codigo: "09", nombre: "DOLORES" },
    { departamentoCodigo: "10", municipioCodigo: "14", codigo: "01", nombre: "APASTEPEQUE" },
    { departamentoCodigo: "10", municipioCodigo: "15", codigo: "02", nombre: "GUADALUPE" },
    { departamentoCodigo: "10", municipioCodigo: "15", codigo: "03", nombre: "SAN CAY ISTEPEQ" },
    { departamentoCodigo: "10", municipioCodigo: "14", codigo: "04", nombre: "SANTA CLARA" },
    { departamentoCodigo: "10", municipioCodigo: "14", codigo: "05", nombre: "SANTO DOMINGO" },
    { departamentoCodigo: "10", municipioCodigo: "14", codigo: "06", nombre: "SN EST CATARINA" },
    { departamentoCodigo: "10", municipioCodigo: "14", codigo: "07", nombre: "SAN ILDEFONSO" },
    { departamentoCodigo: "10", municipioCodigo: "14", codigo: "08", nombre: "SAN LORENZO" },
    { departamentoCodigo: "10", municipioCodigo: "14", codigo: "09", nombre: "SAN SEBASTIÁN" },
    { departamentoCodigo: "10", municipioCodigo: "15", codigo: "10", nombre: "SAN VICENTE" },
    { departamentoCodigo: "10", municipioCodigo: "15", codigo: "11", nombre: "TECOLUCA" },
    { departamentoCodigo: "10", municipioCodigo: "15", codigo: "12", nombre: "TEPETITÁN" },
    { departamentoCodigo: "10", municipioCodigo: "15", codigo: "13", nombre: "VERAPAZ" },
    { departamentoCodigo: "11", municipioCodigo: "24", codigo: "01", nombre: "ALEGRÍA" },
    { departamentoCodigo: "11", municipioCodigo: "24", codigo: "02", nombre: "BERLÍN" },
    { departamentoCodigo: "11", municipioCodigo: "25", codigo: "03", nombre: "CALIFORNIA" },
    { departamentoCodigo: "11", municipioCodigo: "25", codigo: "04", nombre: "CONCEP BATRES" },
    { departamentoCodigo: "11", municipioCodigo: "24", codigo: "05", nombre: "EL TRIUNFO" },
    { departamentoCodigo: "11", municipioCodigo: "25", codigo: "06", nombre: "EREGUAYQUÍN" },
    { departamentoCodigo: "11", municipioCodigo: "24", codigo: "07", nombre: "ESTANZUELAS" },
    { departamentoCodigo: "11", municipioCodigo: "26", codigo: "08", nombre: "JIQUILISCO" },
    { departamentoCodigo: "11", municipioCodigo: "24", codigo: "09", nombre: "JUCUAPA" },
    { departamentoCodigo: "11", municipioCodigo: "25", codigo: "10", nombre: "JUCUARÁN" },
    { departamentoCodigo: "11", municipioCodigo: "24", codigo: "11", nombre: "MERCEDES UMAÑA" },
    { departamentoCodigo: "11", municipioCodigo: "24", codigo: "12", nombre: "NUEVA GRANADA" },
    { departamentoCodigo: "11", municipioCodigo: "25", codigo: "13", nombre: "OZATLÁN" },
    { departamentoCodigo: "11", municipioCodigo: "26", codigo: "14", nombre: "PTO EL TRIUNFO" },
    { departamentoCodigo: "11", municipioCodigo: "26", codigo: "15", nombre: "SAN AGUSTÍN" },
    { departamentoCodigo: "11", municipioCodigo: "24", codigo: "16", nombre: "SN BUENAVENTURA" },
    { departamentoCodigo: "11", municipioCodigo: "25", codigo: "17", nombre: "SAN DIONISIO" },
    { departamentoCodigo: "11", municipioCodigo: "25", codigo: "18", nombre: "SANTA ELENA" },
    { departamentoCodigo: "11", municipioCodigo: "26", codigo: "19", nombre: "SAN FCO JAVIER" },
    { departamentoCodigo: "11", municipioCodigo: "25", codigo: "20", nombre: "SANTA MARÍA" },
    { departamentoCodigo: "11", municipioCodigo: "24", codigo: "21", nombre: "STGO DE MARÍA" },
    { departamentoCodigo: "11", municipioCodigo: "25", codigo: "22", nombre: "TECAPÁN" },
    { departamentoCodigo: "11", municipioCodigo: "25", codigo: "23", nombre: "USULUTÁN" },
    { departamentoCodigo: "12", municipioCodigo: "21", codigo: "01", nombre: "CAROLINA" },
    { departamentoCodigo: "12", municipioCodigo: "21", codigo: "02", nombre: "CIUDAD BARRIOS" },
    { departamentoCodigo: "12", municipioCodigo: "22", codigo: "03", nombre: "COMACARÁN" },
    { departamentoCodigo: "12", municipioCodigo: "21", codigo: "04", nombre: "CHAPELTIQUE" },
    { departamentoCodigo: "12", municipioCodigo: "23", codigo: "05", nombre: "CHINAMECA" },
    { departamentoCodigo: "12", municipioCodigo: "22", codigo: "06", nombre: "CHIRILAGUA" },
    { departamentoCodigo: "12", municipioCodigo: "23", codigo: "07", nombre: "EL TRANSITO" },
    { departamentoCodigo: "12", municipioCodigo: "23", codigo: "08", nombre: "LOLOTIQUE" },
    { departamentoCodigo: "12", municipioCodigo: "22", codigo: "09", nombre: "MONCAGUA" },
    { departamentoCodigo: "12", municipioCodigo: "23", codigo: "10", nombre: "NUEVA GUADALUPE" },
    { departamentoCodigo: "12", municipioCodigo: "21", codigo: "11", nombre: "NVO EDÉN S JUAN" },
    { departamentoCodigo: "12", municipioCodigo: "22", codigo: "12", nombre: "QUELEPA" },
    { departamentoCodigo: "12", municipioCodigo: "21", codigo: "13", nombre: "SAN ANT D MOSCO" },
    { departamentoCodigo: "12", municipioCodigo: "21", codigo: "14", nombre: "SAN GERARDO" },
    { departamentoCodigo: "12", municipioCodigo: "23", codigo: "15", nombre: "SAN JORGE" },
    { departamentoCodigo: "12", municipioCodigo: "21", codigo: "16", nombre: "SAN LUIS REINA" },
    { departamentoCodigo: "12", municipioCodigo: "22", codigo: "17", nombre: "SAN MIGUEL" },
    { departamentoCodigo: "12", municipioCodigo: "23", codigo: "18", nombre: "SAN RAF ORIENTE" },
    { departamentoCodigo: "12", municipioCodigo: "21", codigo: "19", nombre: "SESORI" },
    { departamentoCodigo: "12", municipioCodigo: "22", codigo: "20", nombre: "ULUAZAPA" },
    { departamentoCodigo: "13", municipioCodigo: "27", codigo: "01", nombre: "ARAMBALA" },
    { departamentoCodigo: "13", municipioCodigo: "27", codigo: "02", nombre: "CACAOPERA" },
    { departamentoCodigo: "13", municipioCodigo: "27", codigo: "03", nombre: "CORINTO" },
    { departamentoCodigo: "13", municipioCodigo: "28", codigo: "04", nombre: "CHILANGA" },
    { departamentoCodigo: "13", municipioCodigo: "28", codigo: "05", nombre: "DELIC DE CONCEP" },
    { departamentoCodigo: "13", municipioCodigo: "28", codigo: "06", nombre: "EL DIVISADERO" },
    { departamentoCodigo: "13", municipioCodigo: "27", codigo: "07", nombre: "EL ROSARIO" },
    { departamentoCodigo: "13", municipioCodigo: "28", codigo: "08", nombre: "GUALOCOCTI" },
    { departamentoCodigo: "13", municipioCodigo: "28", codigo: "09", nombre: "GUATAJIAGUA" },
    { departamentoCodigo: "13", municipioCodigo: "27", codigo: "10", nombre: "JOATECA" },
    { departamentoCodigo: "13", municipioCodigo: "27", codigo: "11", nombre: "JOCOAITIQUE" },
    { departamentoCodigo: "13", municipioCodigo: "28", codigo: "12", nombre: "JOCORO" },
    { departamentoCodigo: "13", municipioCodigo: "28", codigo: "13", nombre: "LOLOTIQUILLO" },
    { departamentoCodigo: "13", municipioCodigo: "27", codigo: "14", nombre: "MEANGUERA" },
    { departamentoCodigo: "13", municipioCodigo: "28", codigo: "15", nombre: "OSICALA" },
    { departamentoCodigo: "13", municipioCodigo: "27", codigo: "16", nombre: "PERQUÍN" },
    { departamentoCodigo: "13", municipioCodigo: "28", codigo: "17", nombre: "SAN CARLOS" },
    { departamentoCodigo: "13", municipioCodigo: "27", codigo: "18", nombre: "SAN FERNANDO" },
    { departamentoCodigo: "13", municipioCodigo: "28", codigo: "19", nombre: "SAN FCO GOTERA" },
    { departamentoCodigo: "13", municipioCodigo: "27", codigo: "20", nombre: "SAN ISIDRO" },
    { departamentoCodigo: "13", municipioCodigo: "28", codigo: "21", nombre: "SAN SIMÓN" },
    { departamentoCodigo: "13", municipioCodigo: "28", codigo: "22", nombre: "SENSEMBRA" },
    { departamentoCodigo: "13", municipioCodigo: "28", codigo: "23", nombre: "SOCIEDAD" },
    { departamentoCodigo: "13", municipioCodigo: "27", codigo: "24", nombre: "TOROLA" },
    { departamentoCodigo: "13", municipioCodigo: "28", codigo: "25", nombre: "YAMABAL" },
    { departamentoCodigo: "13", municipioCodigo: "28", codigo: "26", nombre: "YOLOAIQUÍN" },
    { departamentoCodigo: "14", municipioCodigo: "19", codigo: "01", nombre: "ANAMOROS" },
    { departamentoCodigo: "14", municipioCodigo: "19", codigo: "02", nombre: "BOLÍVAR" },
    { departamentoCodigo: "14", municipioCodigo: "19", codigo: "03", nombre: "CONCEP DE OTE" },
    { departamentoCodigo: "14", municipioCodigo: "20", codigo: "04", nombre: "CONCHAGUA" },
    { departamentoCodigo: "14", municipioCodigo: "20", codigo: "05", nombre: "EL CARMEN" },
    { departamentoCodigo: "14", municipioCodigo: "19", codigo: "06", nombre: "EL SAUCE" },
    { departamentoCodigo: "14", municipioCodigo: "20", codigo: "07", nombre: "INTIPUCÁ" },
    { departamentoCodigo: "14", municipioCodigo: "20", codigo: "08", nombre: "LA UNIÓN" },
    { departamentoCodigo: "14", municipioCodigo: "19", codigo: "09", nombre: "LISLIQUE" },
    { departamentoCodigo: "14", municipioCodigo: "20", codigo: "10", nombre: "MEANG DEL GOLFO" },
    { departamentoCodigo: "14", municipioCodigo: "19", codigo: "11", nombre: "NUEVA ESPARTA" },
    { departamentoCodigo: "14", municipioCodigo: "19", codigo: "12", nombre: "PASAQUINA" },
    { departamentoCodigo: "14", municipioCodigo: "19", codigo: "13", nombre: "POLORÓS" },
    { departamentoCodigo: "14", municipioCodigo: "20", codigo: "14", nombre: "SAN ALEJO" },
    { departamentoCodigo: "14", municipioCodigo: "19", codigo: "15", nombre: "SAN JOSE" },
    { departamentoCodigo: "14", municipioCodigo: "19", codigo: "16", nombre: "SANTA ROSA LIMA" },
    { departamentoCodigo: "14", municipioCodigo: "20", codigo: "17", nombre: "YAYANTIQUE" },
    { departamentoCodigo: "14", municipioCodigo: "20", codigo: "18", nombre: "YUCUAIQUÍN" },
  ];

  // ✅ Catálogo CAT-022 Tipos de documento de identificación (receptor Sujeto Excluido)
  const tiposDocumento = [
    { codigo: "36", nombre: "NIT" },
    { codigo: "13", nombre: "DUI" },
    { codigo: "37", nombre: "Otro" },
    { codigo: "03", nombre: "Pasaporte" },
    { codigo: "02", nombre: "Carnet de Residente" },
  ];

  // ✅ FILTRAR MUNICIPIOS según departamento seleccionado (por CÓDIGO)
  const municipiosFiltrados = useMemo(() => {
    if (!form.departamento) {
      return [];
    }
    return todosMunicipios.filter(m => m.departamentoCodigo === form.departamento);
  }, [form.departamento]);

  // ✅ FILTRAR DISTRITOS: dependen de Departamento Y Municipio (cascada real)
  const distritosFiltrados = useMemo(() => {
    if (!form.departamento || !form.municipio) {
      return [];
    }
    return todosDistritos.filter(
      d => d.departamentoCodigo === form.departamento && d.municipioCodigo === form.municipio
    );
  }, [form.departamento, form.municipio]);

  // 🔄 Cargar datos si es edición
  useEffect(() => {
    if (editingSupplier) {
      setForm({
        nombre: editingSupplier.nombre || "",
        nombre_comercial: editingSupplier.nombre_comercial || "",
        contacto: editingSupplier.contacto || "",
        telefono: editingSupplier.telefono || "",
        email: editingSupplier.email || "",
        direccion: editingSupplier.direccion || "",
        departamento: editingSupplier.departamento || "",
        municipio: editingSupplier.municipio || "",
        distrito: editingSupplier.distrito || "",
        nit: editingSupplier.nit || "",
        nrc: editingSupplier.nrc || "",
        giro: editingSupplier.giro || "",
        tipo_documento: editingSupplier.tipo_documento || "36",
        numero_documento: editingSupplier.numero_documento || "",
      });
    }
  }, [editingSupplier]);

  // 🎯 Foco automático en Nombre
  useEffect(() => {
    nombreRef.current?.focus();
  }, []);

  // 🧠 Manejo de inputs
  const handleChange = (e) => {
    const { name, value } = e.target;

    // Si cambia el departamento, limpiar municipio y distrito (los códigos ya no aplican)
    if (name === "departamento") {
      setForm({
        ...form,
        departamento: value,
        municipio: "",
        distrito: "",
      });
    } else if (name === "municipio") {
      // Al cambiar la zona (municipio), el pueblo (distrito) elegido ya no es válido
      setForm({
        ...form,
        municipio: value,
        distrito: "",
      });
    } else {
      setForm({
        ...form,
        [name]: value,
      });
    }
  };

  // 💾 Guardar proveedor
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Un solo campo de identificación en el formulario (tipo_documento + numero_documento).
    // Si el tipo elegido es NIT, también llenamos internamente "nit" para no romper
    // la lógica existente del backend (tieneDatosCompletos, unicidad, etc. leen "nit").
    const payload = {
      ...form,
      nit: form.tipo_documento === "36" ? form.numero_documento : "",
    };

    try {
      if (editingSupplier) {
        await api.put(`/suppliers/${editingSupplier.id}`, payload);
        Swal.fire(
          "Actualizado",
          "Proveedor actualizado correctamente",
          "success"
        );
      } else {
        await api.post("/suppliers", payload);
        Swal.fire("Guardado", "Proveedor creado correctamente", "success");
      }

      onSaved();
    } catch (error) {
      if (error.response?.status === 422) {
        const errors = error.response.data.errors;
        const firstError = Object.values(errors)[0][0];
        Swal.fire("Error de validación", firstError, "error");
      } else {
        Swal.fire("Error", "Ocurrió un error al guardar el proveedor", "error");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card" style={{ marginBottom: "24px" }}>
      <div className="card-header">
        <div className="d-flex align-items-center gap-2">
          <Truck size={24} color="#667eea" />
          <h2 className="card-title" style={{ fontSize: "20px" }}>
            {editingSupplier ? "Editar Proveedor" : "Nuevo Proveedor"}
          </h2>
        </div>
      </div>

      <div className="card-body">
        <form onSubmit={handleSubmit}>
          {/* INFORMACIÓN GENERAL */}
          <h4
            style={{
              fontSize: "16px",
              fontWeight: 600,
              marginBottom: "16px",
              color: "#2d3748",
            }}
          >
            <Building2 size={18} style={{ display: "inline", marginRight: "8px" }} />
            Información General
          </h4>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
              gap: "16px",
              marginBottom: "24px",
            }}
          >
            <div className="form-group">
              <label className="form-label">Nombre *</label>
              <input
                type="text"
                name="nombre"
                className="form-control"
                placeholder="Nombre del proveedor"
                value={form.nombre}
                onChange={handleChange}
                ref={nombreRef}
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">Nombre Comercial</label>
              <input
                type="text"
                name="nombre_comercial"
                className="form-control"
                placeholder="Nombre comercial"
                value={form.nombre_comercial}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Giro o Actividad</label>
              <input
                type="text"
                name="giro"
                className="form-control"
                placeholder="Ej: Distribuidor de alimentos"
                value={form.giro}
                onChange={handleChange}
              />
            </div>
          </div>

          {/* SEPARADOR */}
          <div
            style={{
              borderTop: "2px solid #e2e8f0",
              margin: "24px 0",
            }}
          ></div>

          {/* IDENTIFICACIÓN — único lugar para tipo + número de documento */}
          <h4
            style={{
              fontSize: "16px",
              fontWeight: 600,
              marginBottom: "16px",
              color: "#2d3748",
            }}
          >
            <IdCard size={18} style={{ display: "inline", marginRight: "8px" }} />
            Identificación
          </h4>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: "16px",
              marginBottom: "24px",
            }}
          >
            <div className="form-group">
              <label className="form-label">Tipo de Documento</label>
              <select
                name="tipo_documento"
                className="form-control"
                value={form.tipo_documento}
                onChange={handleChange}
              >
                {tiposDocumento.map((td) => (
                  <option key={td.codigo} value={td.codigo}>{td.nombre}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Número de Documento</label>
              <input
                type="text"
                name="numero_documento"
                className="form-control"
                placeholder={form.tipo_documento === "36" ? "0000-000000-000-0" : "00000000-0"}
                value={form.numero_documento}
                onChange={handleChange}
              />
              <small style={{ color: "#718096", fontSize: "12px" }}>
                Puede incluir guiones (se guardará el formato correcto según el tipo)
              </small>
            </div>

            <div className="form-group">
              <label className="form-label">NRC</label>
              <input
                type="text"
                name="nrc"
                className="form-control"
                placeholder="000000-0"
                value={form.nrc}
                onChange={handleChange}
              />
              <small style={{ color: "#718096", fontSize: "12px" }}>
                Solo aplica si es contribuyente registrado (Crédito Fiscal)
              </small>
            </div>
          </div>

          {/* SEPARADOR */}
          <div
            style={{
              borderTop: "2px solid #e2e8f0",
              margin: "24px 0",
            }}
          ></div>


          {/* INFORMACIÓN DE CONTACTO */}
          <h4
            style={{
              fontSize: "16px",
              fontWeight: 600,
              marginBottom: "16px",
              color: "#2d3748",
            }}
          >
            <User size={18} style={{ display: "inline", marginRight: "8px" }} />
            Información de Contacto
          </h4>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: "16px",
              marginBottom: "24px",
            }}
          >
            <div className="form-group">
              <label className="form-label">
                <User size={14} style={{ display: "inline", marginRight: "4px" }} />
                Persona de Contacto
              </label>
              <input
                type="text"
                name="contacto"
                className="form-control"
                placeholder="Nombre del contacto"
                value={form.contacto}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                <Phone size={14} style={{ display: "inline", marginRight: "4px" }} />
                Teléfono
              </label>
              <input
                type="text"
                name="telefono"
                className="form-control"
                placeholder="0000-0000"
                value={form.telefono}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                <Mail size={14} style={{ display: "inline", marginRight: "4px" }} />
                Correo Electrónico
              </label>
              <input
                type="email"
                name="email"
                className="form-control"
                placeholder="correo@ejemplo.com"
                value={form.email}
                onChange={handleChange}
              />
            </div>
          </div>

          {/* SEPARADOR */}
          <div
            style={{
              borderTop: "2px solid #e2e8f0",
              margin: "24px 0",
            }}
          ></div>

          {/* DIRECCIÓN */}
          <h4
            style={{
              fontSize: "16px",
              fontWeight: 600,
              marginBottom: "16px",
              color: "#2d3748",
            }}
          >
            Dirección
          </h4>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "2fr 1fr 1fr 1fr",
              gap: "16px",
              marginBottom: "24px",
            }}
          >
            <div className="form-group">
              <label className="form-label">Dirección Completa</label>
              <input
                type="text"
                name="direccion"
                className="form-control"
                placeholder="Dirección del proveedor"
                value={form.direccion}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Departamento</label>
              <select
                name="departamento"
                className="form-control"
                value={form.departamento}
                onChange={handleChange}
              >
                <option value="">Seleccione...</option>
                {departamentos.map((depto) => (
                  <option key={depto.codigo} value={depto.codigo}>
                    {depto.nombre}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Municipio</label>
              <select
                name="municipio"
                className="form-control"
                value={form.municipio}
                onChange={handleChange}
                disabled={!form.departamento}
              >
                <option value="">
                  {form.departamento ? "Seleccione..." : "Primero seleccione departamento"}
                </option>
                {municipiosFiltrados.map((muni) => (
                  <option key={muni.codigo} value={muni.codigo}>
                    {muni.nombre}
                  </option>
                ))}
              </select>
              {form.departamento && municipiosFiltrados.length === 0 && (
                <small style={{ color: "#e53e3e", fontSize: "12px", display: "block", marginTop: "4px" }}>
                  ⚠️ No hay municipios disponibles para este departamento
                </small>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">Distrito</label>
              <select
                name="distrito"
                className="form-control"
                value={form.distrito}
                onChange={handleChange}
                disabled={!form.municipio}
              >
                <option value="">
                  {form.municipio ? "Seleccione..." : "Primero seleccione municipio"}
                </option>
                {distritosFiltrados.map((dist) => (
                  <option key={dist.codigo} value={dist.codigo}>
                    {dist.nombre}
                  </option>
                ))}
              </select>
              {form.municipio && distritosFiltrados.length === 0 && (
                <small style={{ color: "#e53e3e", fontSize: "12px", display: "block", marginTop: "4px" }}>
                  ⚠️ No hay distritos disponibles para este municipio
                </small>
              )}
            </div>
          </div>

          {/* BOTONES */}
          <div className="d-flex gap-2" style={{ marginTop: "32px" }}>
            <button
              type="submit"
              className="btn btn-success"
              disabled={loading}
            >
              <Save size={18} />
              {loading
                ? "Guardando..."
                : editingSupplier
                ? "Actualizar Proveedor"
                : "Guardar Proveedor"}
            </button>

            {onCancel && (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onCancel}
                disabled={loading}
              >
                <X size={18} />
                Cancelar
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}